// Pure mutators for the box / box-item slice of a DraftScratchpad.
// Extracted from `store.ts` (#701) so the store stays under the
// 600-line cap. Each function takes the current scratchpad and returns
// the next one, or `null` when the scratchpad is absent (the store
// callback then returns the existing state unchanged).
//
// Box client ids are minted by the caller (the store) because zustand
// `set` is the only place that should observe the new id — the helpers
// stay pure and id-allocation-free.

import type { DraftBoxVolumeMode, DraftScratchpad } from './types.js';

export function addBox(d: DraftScratchpad | null, clientBoxId: string): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxesSectionOpen: true,
    boxes: [
      ...d.boxes,
      {
        id: null,
        clientBoxId,
        count: 1,
        weightKg: 0,
        volumeM3: 0,
        volumeMode: 'direct' as DraftBoxVolumeMode,
        widthCm: null,
        depthCm: null,
        heightCm: null,
        items: [],
      },
    ],
  };
}

export function patchBox(
  d: DraftScratchpad | null,
  boxClientId: string,
  p: Partial<DraftScratchpad['boxes'][number]>,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxes: d.boxes.map((b) => (b.clientBoxId === boxClientId ? { ...b, ...p } : b)),
  };
}

export function removeBox(d: DraftScratchpad | null, boxClientId: string): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxes: d.boxes.filter((b) => b.clientBoxId !== boxClientId),
  };
}

export function addBoxItem(
  d: DraftScratchpad | null,
  boxClientId: string,
  clientBoxItemId: string,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxes: d.boxes.map((b) =>
      b.clientBoxId === boxClientId
        ? {
            ...b,
            items: [
              ...b.items,
              {
                id: null,
                clientBoxItemId,
                // WHY (#704): newly added rows join the first group by
                //   default. The logist re-assigns via the per-row dropdown.
                groupOrdinal: 0,
                name: '',
                brand: '',
                material: '',
                description: '',
                quantity: null,
                photos: [],
              },
            ],
          }
        : b,
    ),
  };
}

export function patchBoxItem(
  d: DraftScratchpad | null,
  boxClientId: string,
  boxItemClientId: string,
  p: Partial<DraftScratchpad['boxes'][number]['items'][number]>,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxes: d.boxes.map((b) =>
      b.clientBoxId === boxClientId
        ? {
            ...b,
            items: b.items.map((it) =>
              it.clientBoxItemId === boxItemClientId ? { ...it, ...p } : it,
            ),
          }
        : b,
    ),
  };
}

export function removeBoxItem(
  d: DraftScratchpad | null,
  boxClientId: string,
  boxItemClientId: string,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxes: d.boxes.map((b) =>
      b.clientBoxId === boxClientId
        ? {
            ...b,
            items: b.items.filter((it) => it.clientBoxItemId !== boxItemClientId),
          }
        : b,
    ),
  };
}

export function setBoxItemPhoto(
  d: DraftScratchpad | null,
  boxClientId: string,
  boxItemClientId: string,
  key: string | null,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    boxes: d.boxes.map((b) =>
      b.clientBoxId === boxClientId
        ? {
            ...b,
            items: b.items.map((it) =>
              it.clientBoxItemId === boxItemClientId
                ? { ...it, photos: key === null ? [] : [key] }
                : it,
            ),
          }
        : b,
    ),
  };
}
